import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { Request } from 'express';

export interface Language {
    code: string;
    name: string;
    flag: string;
}

export const SUPPORTED_LANGUAGES = ['en', 'nl', 'ms'];
export const DEFAULT_LANGUAGE = 'nl';

@Injectable({
    providedIn: 'root'
})
export class LanguageService {
    private readonly STORAGE_KEY = 'trainerswap_language';
    private isBrowser: boolean;

    languages: Language[] = [
        { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
        { code: 'en', name: 'English', flag: '🇬🇧' },
        { code: 'ms', name: 'Bahasa Malaysia', flag: '🇲🇾' }
    ];

    currentLanguage$ = new BehaviorSubject<Language>(this.languages[0]);

    constructor(
        private translate: TranslateService,
        private router: Router,
        @Inject(PLATFORM_ID) platformId: object,
        @Optional() @Inject(REQUEST) private request: Request
    ) {
        this.isBrowser = isPlatformBrowser(platformId);
        this.initLanguage();
    }

    private initLanguage(): void {
        // Set available languages
        this.translate.addLangs(this.languages.map(l => l.code));

        // Try to get language from URL first
        const urlLang = this.getLanguageFromUrl();
        const savedLang = this.isBrowser ? localStorage.getItem(this.STORAGE_KEY) : null;
        const browserLang = this.translate.getBrowserLang();

        let langCode = urlLang || savedLang || browserLang || DEFAULT_LANGUAGE;

        // Validate language code
        if (!this.languages.find(l => l.code === langCode)) {
            langCode = DEFAULT_LANGUAGE;
        }

        this.setLanguage(langCode, false); // Don't navigate on init
    }

    private getLanguageFromUrl(): string | null {
        // On server, use the request URL
        if (!this.isBrowser && this.request) {
            const path = this.request.url || this.request.originalUrl || '';
            const segments = path.split('/').filter(s => s);
            if (segments.length > 0 && SUPPORTED_LANGUAGES.includes(segments[0])) {
                return segments[0];
            }
            return null;
        }

        // On browser, use window.location
        if (this.isBrowser && typeof window !== 'undefined') {
            const path = window.location.pathname;
            const segments = path.split('/').filter(s => s);
            if (segments.length > 0 && SUPPORTED_LANGUAGES.includes(segments[0])) {
                return segments[0];
            }
        }
        return null;
    }

    setLanguage(code: string, navigate: boolean = true): void {
        const language = this.languages.find(l => l.code === code);
        if (language) {
            this.translate.use(code);
            if (this.isBrowser) {
                localStorage.setItem(this.STORAGE_KEY, code);
            }
            this.currentLanguage$.next(language);

            // Navigate to new language URL if requested (only on browser)
            if (navigate && this.isBrowser && typeof window !== 'undefined') {
                const currentPath = this.router.url;
                const segments = currentPath.split('/').filter(s => s);

                // Remove old language prefix if present
                if (segments.length > 0 && SUPPORTED_LANGUAGES.includes(segments[0])) {
                    segments.shift();
                }

                // Navigate to new language path
                const newPath = `/${code}/${segments.join('/')}`;
                this.router.navigateByUrl(newPath);
            }
        }
    }

    getCurrentLanguage(): Language {
        return this.currentLanguage$.getValue();
    }

    getLanguages(): Language[] {
        return this.languages;
    }
}
